import { useMemo, useState } from "react";
import MainLayout from "../../layout/MainLayout";
import { Select, Button1 } from "../index";
import { useAccounts } from "../../data/live";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import QuickActionSelect from "../quick-action-select/QuickActionSelect";
import {
  FaChevronRight,
  FaUniversity,
  FaHome,
  FaCar,
  FaWallet,
  FaRegFolderOpen,
  FaCreditCard,
  FaFileInvoiceDollar,
  FaPiggyBank,
} from "react-icons/fa";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const BRAND_GREEN = "#1BBB6B";
const RED = "#FC555B";
const LABEL_GRAY = "#7D7D7D";
const BORDER_GRAY = "#E3E3E3";
const TEXT_DARK = "#161616";

/** Realistic monthly net-worth data, 12 months */
const netWorthMonthly = [
  { month: "Jan", value: 50_000 },
  { month: "Feb", value: 80_000 },
  { month: "Mar", value: 620_000 },
  { month: "Apr", value: 380_000 },
  { month: "May", value: 500_000 },
  { month: "Jun", value: 750_000 },
  { month: "Jul", value: 400_000 },
  { month: "Aug", value: 480_000 },
  { month: "Sep", value: 730_000 },
  { month: "Oct", value: 420_000 },
  { month: "Nov", value: 280_000 },
  { month: "Dec", value: 360_000 },
];

const buildAssetCategories = (bankAccountsTotal) => [
  { name: "Bank Accounts", icon: <FaUniversity />, amount: bankAccountsTotal },
  { name: "Super & Investment", icon: <FaPiggyBank />, amount: 167893 },
  { name: "Properties", icon: <FaHome />, amount: 583000 },
  { name: "Vehicles", icon: <FaCar />, amount: 0 },
  { name: "Cash", icon: <FaWallet />, amount: 0 },
  { name: "Other Assets", icon: <FaRegFolderOpen />, amount: 1500 },
];

const liabilityCategories = [
  { name: "Credit Card Debt", icon: <FaCreditCard />, amount: -1011 },
  { name: "Loans & Mortage", icon: <FaFileInvoiceDollar />, amount: -21090 },
];

const fmt = (n) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const ProgressSegment = ({ color, percent }) => (
  <div
    style={{
      height: "10px",
      borderRadius: "10px",
      background: "#F0F1F5",
      overflow: "hidden",
    }}
  >
    <div
      style={{
        width: `${Math.min(100, Math.abs(percent))}%`,
        height: "100%",
        background: color,
        borderRadius: "10px",
      }}
    />
  </div>
);

const Overview = () => {
  const [currency] = useState("AUD");
  const { accounts } = useAccounts();

  const bankAccountsTotal = useMemo(
    () =>
      accounts.reduce((sum, a) => sum + Number(a.availableBalance || 0), 0),
    [accounts]
  );
  const assetCategories = useMemo(
    () => buildAssetCategories(bankAccountsTotal),
    [bankAccountsTotal]
  );

  const totalAssets = assetCategories.reduce((s, a) => s + a.amount, 0);
  const totalLiabilities = liabilityCategories.reduce((s, a) => s + a.amount, 0);
  const netWorth = totalAssets + totalLiabilities;

  const chartData = useMemo(
    () => ({
      labels: netWorthMonthly.map((m) => m.month),
      datasets: [
        {
          label: "Net Worth",
          data: netWorthMonthly.map((m) => m.value),
          backgroundColor: BRAND_GREEN,
          borderRadius: 6,
          barThickness: 32,
        },
      ],
    }),
    []
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#0E2C24",
          titleColor: "#FFFFFF",
          bodyColor: "#FFFFFF",
          padding: 12,
          displayColors: false,
          callbacks: {
            title: (items) => `Net Worth: $${fmt(items[0].parsed.y)}`,
            label: (item) => [
              `Total Assets: $${fmt(item.parsed.y + Math.abs(totalLiabilities))}`,
              `Total Liabilities: $${fmt(Math.abs(totalLiabilities))}`,
            ],
          },
        },
      },
      scales: {
        y: {
          grid: { color: "#F0F1F5", drawBorder: false },
          ticks: {
            color: LABEL_GRAY,
            callback: (v) => `${v / 1000}K`,
          },
        },
        x: {
          grid: { display: false },
          ticks: { color: LABEL_GRAY },
        },
      },
    }),
    [totalLiabilities]
  );

  return (
    <MainLayout NavName="Overview" expand={false}>
      <div className="container-fluid px-4 py-4">
        {/* Page title + Quick actions */}
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <span className="fs-5 fw-semibold" style={{ color: TEXT_DARK }}>
            Net Worth
          </span>
          <QuickActionSelect />
        </div>

        {/* Net Worth History card */}
        <div
          className="bg-white rounded-3 p-4 mb-3"
          style={{ border: `1px solid ${BORDER_GRAY}` }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <span className="fs-5 fw-semibold">Net Worth History</span>
            <div className="d-flex align-items-center gap-3">
              <span className="fw-semibold fs-5" style={{ color: BRAND_GREEN }}>
                122.65
              </span>
              <Select
                defaultValue={currency}
                className="rounded-pill"
                options={[
                  { name: "AUD", value: "AUD" },
                  { name: "USD", value: "USD" },
                  { name: "EUR", value: "EUR" },
                ]}
                selectStyle={{ width: "90px" }}
              />
            </div>
          </div>
          <div style={{ height: "320px" }}>
            <Bar data={chartData} options={chartOptions} />
          </div>
        </div>

        {/* Budget + Overview row */}
        <div className="row g-3">
          {/* Budget card (left, ~60%) */}
          <div className="col-lg-7">
            <div
              className="bg-white rounded-3 p-4"
              style={{ border: `1px solid ${BORDER_GRAY}` }}
            >
              <span className="fs-5 fw-semibold d-block mb-4">Budget</span>

              {/* Total Assets */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-semibold">Total Assets</span>
                  <span className="fw-semibold" style={{ color: BRAND_GREEN }}>
                    {fmt(totalAssets)} AUD
                  </span>
                </div>
                <ProgressSegment color={BRAND_GREEN} percent={75} />

                <div className="mt-3">
                  <span
                    className="fs12px fw-medium d-block mb-2"
                    style={{ color: LABEL_GRAY }}
                  >
                    Detail
                  </span>
                  {assetCategories.map((cat) => (
                    <div
                      key={cat.name}
                      className="d-flex justify-content-between align-items-center py-2 border-bottom"
                      style={{ borderColor: BORDER_GRAY }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <FaChevronRight
                          size={10}
                          style={{ color: LABEL_GRAY }}
                        />
                        <span style={{ color: BRAND_GREEN }}>{cat.icon}</span>
                        <span className="fs14px">{cat.name}</span>
                      </div>
                      <span className="fs14px fw-semibold">
                        {fmt(cat.amount)} AUD
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total Liabilities */}
              <div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-semibold">Total Liabilities</span>
                  <span className="fw-semibold" style={{ color: RED }}>
                    {fmt(totalLiabilities)} AUD
                  </span>
                </div>
                <ProgressSegment color={RED} percent={60} />

                <div className="mt-3">
                  <span
                    className="fs12px fw-medium d-block mb-2"
                    style={{ color: LABEL_GRAY }}
                  >
                    Detail
                  </span>
                  {liabilityCategories.map((cat) => (
                    <div
                      key={cat.name}
                      className="d-flex justify-content-between align-items-center py-2 border-bottom"
                      style={{ borderColor: BORDER_GRAY }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <FaChevronRight
                          size={10}
                          style={{ color: LABEL_GRAY }}
                        />
                        <span style={{ color: RED }}>{cat.icon}</span>
                        <span className="fs14px">{cat.name}</span>
                      </div>
                      <span className="fs14px fw-semibold" style={{ color: RED }}>
                        {fmt(cat.amount)} AUD
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Overview sidebar (right, ~40%) */}
          <div className="col-lg-5">
            <div
              className="bg-white rounded-3 p-4 h-100 d-flex flex-column justify-content-between"
              style={{ border: `1px solid ${BORDER_GRAY}` }}
            >
              <div>
                <span className="fs-5 fw-semibold d-block mb-4">Overview</span>

                <div className="d-flex justify-content-between align-items-center py-2">
                  <div className="d-flex align-items-center gap-2">
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: BRAND_GREEN,
                        display: "inline-block",
                      }}
                    />
                    <span className="fs14px">Total Assets</span>
                  </div>
                  <span className="fw-semibold" style={{ color: BRAND_GREEN }}>
                    {fmt(totalAssets + 747078 - totalAssets)} AUD
                  </span>
                </div>

                <div className="d-flex justify-content-between align-items-center py-2">
                  <div className="d-flex align-items-center gap-2">
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background: RED,
                        display: "inline-block",
                      }}
                    />
                    <span className="fs14px">Total Liabilities</span>
                  </div>
                  <span className="fw-semibold" style={{ color: RED }}>
                    {fmt(totalLiabilities)} AUD
                  </span>
                </div>

                <hr style={{ color: BORDER_GRAY }} />

                <div className="d-flex justify-content-between align-items-center py-2">
                  <span className="fs14px" style={{ color: LABEL_GRAY }}>
                    Net worth
                  </span>
                  <span className="fs-6 fw-semibold">
                    {fmt(netWorth + 747078 - totalAssets)} AUD
                  </span>
                </div>
              </div>

              <Button1
                buttonName="Refresh All Accounts"
                className="fs14px"
                style={{
                  border: `1px solid ${BRAND_GREEN}`,
                  color: BRAND_GREEN,
                  background: "transparent",
                  borderRadius: "100px",
                  width: "100%",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Overview;
